import React, { useState, useEffect } from 'react';
import { Trans, t } from '@lingui/macro';
import { ArrowLeft, Tag, Calendar, Trash2, Edit3, Check, X, Loader2, FolderOpen, RefreshCw, FileText, Mic, Square } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { AiMemoDoc } from '../../services/FireClientAiMemo';
import { AiTag, MemoCategory, getDefaultCategories } from '../../models/AiMemo';
import { analyzeMemo, reanalyzeTags } from '../../services/AiMemoAnalyzer';
import { cn } from '../../lib/utils';
import { useSpeechToText } from '../../hooks/useSpeechToText';
import VoiceWaveform from './VoiceWaveform';
import { IconPlus, IconSparkles } from '../components/FigmaIcons';

interface AiMemoDetailProps {
    roomId: string;
    memoId: string;
    onBack: () => void;
}

const AiMemoDetail: React.FC<AiMemoDetailProps> = ({ roomId, memoId, onBack }) => {
    const [memo, setMemo] = useState<AiMemoDoc | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(true);
    const [editTitle, setEditTitle] = useState('');
    const [editContent, setEditContent] = useState('');
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [newTag, setNewTag] = useState('');
    const [customCategories, setCustomCategories] = useState<MemoCategory[]>([]);
    const [contentTab, setContentTab] = useState<'original' | 'summary'>('original');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // roomId from props

    // Firestore에서 커스텀 카테고리 로드
    useEffect(() => {
        if (!roomId) return;
        Fire.aiMemo.getCustomCategories(roomId).then(setCustomCategories);
    }, [roomId]);

    // 실시간 구독으로 AI 분석 결과 자동 반영
    useEffect(() => {
        if (!roomId || !memoId) return;

        setIsLoading(true);
        let isFirstLoad = true;

        // 단건 구독으로 AI 분석 결과가 도착하면 자동 갱신
        const unsub = Fire.aiMemo.subscribeToMemos(roomId, (memos) => {
            const found = memos.find(m => m.id === memoId);
            if (found) {
                setMemo(found);
                // 첫 로드 시에만 편집 필드 초기화
                if (isFirstLoad) {
                    setEditTitle(found.title || '');
                    setEditContent(found.content || '');
                    isFirstLoad = false;
                }
            }
            setIsLoading(false);
        });

        return () => unsub();
    }, [roomId, memoId]);

    const handleSave = async () => {
        if (!memo) return;
        setSaveStatus('saving');
        try {
            await Fire.aiMemo.updateMemo(roomId, memoId, {
                title: editTitle.trim(),
                content: editContent.trim(),
            });
            setMemo({ ...memo, title: editTitle.trim(), content: editContent.trim() });
            setIsEditing(false);

            // 내용이 변경되었으면 태그 재분석
            if (editContent.trim() !== memo.content) {
                setIsAnalyzing(true);
                reanalyzeTags(editContent.trim(), memo.tags || [])
                    .then(async (result) => {
                        await Fire.aiMemo.updateMemo(roomId, memoId, {
                            tags: result.tags,
                            category: result.category,
                            summary: result.summary,
                        } as any);
                    })
                    .catch(err => console.warn('[AI Memo] Reanalysis failed:', err))
                    .finally(() => setIsAnalyzing(false));
            }

            setSaveStatus('success');
            setTimeout(() => setSaveStatus('idle'), 1500);
        } catch (e) {
            console.error('Failed to update memo:', e);
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 2000);
        }
    };

    const userLang = My.user?.lang === 'ja' ? 'ja-JP' : My.user?.lang === 'en' ? 'en-US' : 'ko-KR';

    const handleReanalyze = async () => {
        if (!memo) return;
        setIsAnalyzing(true);
        try {
            // 편집 중이면 먼저 저장
            let currentContent = memo.content;
            let currentTitle = memo.title;
            if (isEditing && editContent.trim() !== memo.content) {
                await Fire.aiMemo.updateMemo(roomId, memoId, {
                    title: editTitle.trim(),
                    content: editContent.trim(),
                });
                currentContent = editContent.trim();
                currentTitle = editTitle.trim();
                setIsEditing(false);
            }

            const result = await analyzeMemo(currentContent, currentTitle, userLang);

            // 수동 추가 태그만 보존 (source === 'manual'), AI 태그는 새 결과로 교체
            const manualTags = (memo.tags || []).filter(t => t.source === 'manual');
            const mergedTags = [...result.tags, ...manualTags];

            await Fire.aiMemo.updateMemo(roomId, memoId, {
                category: result.category,
                tags: mergedTags,
                summary: result.summary,
                ...(result.suggestedTitle && !currentTitle ? { title: result.suggestedTitle } : {}),
            } as any);
        } catch (e) {
            console.error('[AI Memo] Re-analyze failed:', e);
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleDelete = async () => {
        try {
            await Fire.aiMemo.deleteMemo(roomId, memoId);
            onBack();
        } catch (e) {
            console.error('Failed to delete memo:', e);
        }
    };

    const handleCategoryChange = async (categoryId: string) => {
        if (!memo) return;
        try {
            await Fire.aiMemo.updateMemo(roomId, memoId, { category: categoryId } as any);
        } catch (e) {
            console.error('Failed to update category:', e);
        }
    };

    const handleTagToggle = async (index: number) => {
        if (!memo) return;
        const newTags = [...memo.tags];
        newTags[index] = { ...newTags[index], isActive: !newTags[index].isActive };
        try {
            await Fire.aiMemo.updateMemo(roomId, memoId, { tags: newTags } as any);
        } catch (e) {
            console.error('Failed to toggle tag:', e);
        }
    };

    const handleTagRemove = async (index: number) => {
        if (!memo) return;
        const newTags = memo.tags.filter((_, i) => i !== index);
        try {
            await Fire.aiMemo.updateMemo(roomId, memoId, { tags: newTags } as any);
        } catch (e) {
            console.error('Failed to remove tag:', e);
        }
    };

    const handleTagAdd = async () => {
        if (!memo || !newTag.trim()) return;
        const tag: AiTag = {
            name: newTag.trim(),
            isActive: true,
            source: 'manual',
            createdAt: new Date(),
        };
        const newTags = [...(memo.tags || []), tag];
        try {
            await Fire.aiMemo.updateMemo(roomId, memoId, { tags: newTags } as any);
            setNewTag('');
        } catch (e) {
            console.error('Failed to add tag:', e);
        }
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString();
    };

    // ========== Loading ==========
    if (isLoading) {
        return (
            <div className="p-5">
                <div className="text-center py-16">
                    <div className="w-8 h-8 border-2 border-border border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-sm text-muted-foreground mt-3"><Trans>Loading memos...</Trans></p>
                </div>
            </div>
        );
    }

    if (!memo) {
        return (
            <div className="p-5">
                <div className="text-center py-16 text-muted-foreground">
                    <IconSparkles className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm"><Trans>Memo not found.</Trans></p>
                    <Button variant="outline" className="mt-4" onClick={onBack}>
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        <Trans>Back to list</Trans>
                    </Button>
                </div>
            </div>
        );
    }


    const allCategories = [...getDefaultCategories(), ...customCategories];
    const cat = allCategories.find(c => c.id === memo.category);
    const hasSummary = !!memo.summary;
    const hasTags = memo.tags && memo.tags.length > 0;


    return (
        <div className="p-5 space-y-5 overflow-y-auto max-h-[90vh]">
            {/* Header: 닫기(좌) — 날짜 — 삭제+저장(우) */}
            <div className="flex items-center justify-between">
                <Button variant="ghost" size="icon" onClick={onBack} title={t`Close`}>
                    <X className="w-4 h-4" />
                </Button>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(memo.created)}
                </div>
                <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setShowDeleteConfirm(true)} className="text-destructive hover:text-destructive" title={t`Delete`}>
                        <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSave}
                        disabled={saveStatus !== 'idle'}
                        className="text-foreground font-semibold"
                        title={t`Save`}
                    >
                        {saveStatus === 'saving' ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> :
                            saveStatus === 'success' ? <Check className="w-4 h-4 text-green-500" /> :
                                saveStatus === 'error' ? <X className="w-4 h-4 text-destructive" /> :
                                    <Trans>Save</Trans>}
                    </Button>
                </div>
            </div>

            {/* Title */}
            <Input
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                placeholder={t`Title`}
                variant="basic"
                className="text-xl font-bold"
            />

            {/* =================== 원문 / AI 요약 탭 =================== */}
            <div className="space-y-3">
                {/* 탭 버튼 */}
                <div className="flex items-center gap-1 border-b">
                    <button
                        onClick={() => setContentTab('original')}
                        className={cn(
                            "flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-all border-b-2 -mb-px",
                            contentTab === 'original'
                                ? "border-border text-foreground"
                                : "border-transparent text-muted-foreground hover:text-card-foreground"
                        )}
                    >
                        <FileText className="w-3.5 h-3.5" />
                        <Trans>Original</Trans>
                    </button>
                    <button
                        onClick={() => setContentTab('summary')}
                        className={cn(
                            "flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-all border-b-2 -mb-px",
                            contentTab === 'summary'
                                ? "border-ai text-ai"
                                : "border-transparent text-muted-foreground hover:text-card-foreground"
                        )}
                    >
                        <IconSparkles className="w-3.5 h-3.5" />
                        <Trans>AI Summary</Trans>
                        {isAnalyzing && <Loader2 className="w-3 h-3 animate-spin ml-1" />}
                    </button>
                </div>

                {/* 탭 콘텐츠 — 동일 높이 */}
                <div className="h-[300px]">
                    {contentTab === 'original' && (
                        <Textarea
                            value={editContent}
                            onChange={e => setEditContent(e.target.value)}
                            className="resize-none text-sm h-full"
                        />
                    )}
                    {contentTab === 'summary' && (
                        <div className="flex flex-col h-full gap-3">
                            <div className="flex-1 overflow-y-auto min-h-0">
                                {isAnalyzing ? (
                                    <div className="flex items-center gap-2 text-xs text-purple-500 py-4 justify-center h-full">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <Trans>AI is analyzing...</Trans>
                                    </div>
                                ) : hasSummary ? (
                                    <div className="p-4 bg-ai-bg rounded-xl border border-ai/20 h-full overflow-y-auto">
                                        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{memo.summary}</p>
                                    </div>
                                ) : (
                                    <div className="text-center py-6 text-muted-foreground">
                                        <IconSparkles className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                                        <p className="text-xs"><Trans>No summary yet.</Trans></p>
                                    </div>
                                )}
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full text-xs shrink-0"
                                onClick={handleReanalyze}
                                disabled={isAnalyzing}
                            >
                                <RefreshCw className={cn("w-3.5 h-3.5 mr-1.5", isAnalyzing && "animate-spin")} />
                                {isAnalyzing ? <Trans>Analyzing...</Trans> : <Trans>Re-analyze</Trans>}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* =================== 카테고리 선택 =================== */}
            <div className="space-y-2">
                <h4 className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                    <FolderOpen className="w-3.5 h-3.5" />
                    <Trans>Category</Trans>
                </h4>
                <div className="flex flex-wrap gap-2">
                    {allCategories.map(c => (
                        <button
                            key={c.id}
                            onClick={() => handleCategoryChange(c.id)}
                            className={cn(
                                "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border",
                                memo.category === c.id
                                    ? "bg-primary text-on-primary-text border-primary shadow-sm"
                                    : "bg-background text-foreground border-border hover:border-border hover:bg-muted/50"
                            )}
                        >
                            <span>{c.icon}</span>
                            <span>{c.name}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* =================== 태그 관리 =================== */}
            <div className="space-y-2">
                <h4 className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" />
                    <Trans>Tags</Trans>
                    {hasTags && (
                        <span className="text-meta-sm font-normal text-muted-foreground ml-1">
                            ({memo.tags.filter(t => t.isActive).length}<Trans> active</Trans>)
                        </span>
                    )}
                </h4>

                {hasTags && (
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {memo.tags.map((tag, i) => (
                            <span
                                key={i}
                                className={cn(
                                    "group flex items-center text-sm cursor-pointer transition-opacity",
                                    tag.isActive
                                        ? "text-foreground hover:text-foreground"
                                        : "text-muted-foreground line-through hover:text-muted-foreground",
                                )}
                                onClick={() => handleTagToggle(i)}
                            >
                                {tag.isNew && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-1 flex-shrink-0" />}
                                <span>#{tag.name}</span>
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleTagRemove(i); }}
                                    className={cn(
                                        "w-3.5 h-3.5 flex items-center justify-center rounded-full text-destructive hover:text-destructive ml-1 flex-shrink-0",
                                        "opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity"
                                    )}
                                >
                                    <X className="w-2.5 h-2.5" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {/* 태그 수동 추가 */}
                <div className="flex items-center gap-2">
                    <Input
                        value={newTag}
                        onChange={e => setNewTag(e.target.value)}
                        placeholder={t`Add tags...`}
                        variant="basic"
                        className="flex-1 h-8 text-xs"
                        onKeyDown={e => e.key === 'Enter' && handleTagAdd()}
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-xs"
                        onClick={handleTagAdd}
                        disabled={!newTag.trim()}
                    >
                        <IconPlus className="w-3 h-3 mr-1" />
                        <Trans>Add</Trans>
                    </Button>
                </div>
            </div>



            {/* =================== 음성으로 메모 추가 =================== */}
            <VoiceAppendSection
                onAppend={(text: string) => setEditContent(prev => prev + (prev ? '\n' : '') + text)}
            />

            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle><Trans>Delete this memo?</Trans></AlertDialogTitle>
                        <AlertDialogDescription>
                            <Trans>This action cannot be undone.</Trans>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">
                            <Trans>Delete</Trans>
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

/** 음성으로 기존 메모에 내용 추가하는 서브 컴포넌트 */
const VoiceAppendSection: React.FC<{ onAppend: (text: string) => void }> = ({ onAppend }) => {
    const sttLang = My.user?.lang === 'ja' ? 'ja-JP' : My.user?.lang === 'en' ? 'en-US' : 'ko-KR';
    const {
        isListening, transcript, interimTranscript,
        startListening, stopListening, resetTranscript, audioStream, isConverting,
    } = useSpeechToText({ lang: sttLang });

    const handleToggle = () => {
        if (isListening) {
            stopListening();
        } else {
            resetTranscript();
            startListening();
        }
    };

    const handleAppend = () => {
        const text = transcript.trim();
        if (!text) return;
        onAppend(text);
        resetTranscript();
    };

    return (
        <div className="space-y-3 pt-3 border-t">
            <h4 className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                <Mic className="w-3.5 h-3.5" />
                <Trans>Add by voice</Trans>
            </h4>
            <div className="flex flex-col items-center gap-3">
                <button
                    onClick={handleToggle}
                    className={cn(
                        "w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-md border",
                        isListening
                            ? "border-destructive bg-destructive hover:bg-destructive/90 animate-pulse shadow-destructive/50"
                            : "bg-background border-border hover:bg-card"
                    )}
                >
                    {isListening ? <Square className="w-6 h-6 text-on-primary-text" /> : <Mic className="w-6 h-6 text-foreground" />}
                </button>
                <VoiceWaveform isActive={isListening} audioStream={audioStream} />
                <p className="text-meta-sm text-muted-foreground">
                    {isListening ? t`Recording... Tap to stop` : t`Tap to add voice memo`}
                </p>
            </div>

            {(transcript || interimTranscript || isConverting) && (
                <div className="p-3 bg-muted/50 rounded-lg border text-sm">
                    <p className="whitespace-pre-wrap">
                        {transcript}
                        {interimTranscript && <span className="text-muted-foreground">{interimTranscript}</span>}
                    </p>
                    {isConverting && (
                        <div className="flex items-center gap-2 mt-2 text-xs text-purple-500">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            {t`Converting speech to text...`}
                        </div>
                    )}
                </div>
            )}

            {transcript.trim() && !isListening && (
                <Button size="sm" onClick={handleAppend} className="w-full">
                    <IconPlus className="w-3.5 h-3.5 mr-1" />
                    <Trans>Add to original</Trans>
                </Button>
            )}
        </div>
    );
};

export default AiMemoDetail;
